import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, Validators, FormBuilder, FormsModule, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { CustomDatePipe } from './../pipes/customdate.pipe';
import { formFieldsUpdated as defaultFormFieldsUpdated, isNameKeyAllowed } from '../commonfunctions/commonFunction';
import { toLocalDate } from '../utils/date-utils';

@Component({
  selector: 'app-forms-model',
  templateUrl: './forms-model.component.html',
  styleUrl: './forms-model.component.scss'
})
export class FormsModelComponent {
  myDateValue: Date | undefined;
  Object = Object;
  date: Date = new Date();
  bsConfig: Partial<BsDatepickerConfig>;

  minDate: Date = new Date();
  maxDate: Date = new Date();

  @Input() show = false;
  @Input() formConfigData: any;
  @Input() allValidationsFormConfig: any;
  @Input() currFormType: any;
  @Input() editChildData: any;
  @Input() buttonInfo: any;
  @Input() languageData: any;
  @Input() childFieldsConfig: any = {};

  @Output() submitForm = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();

  public languageTokensArray: any = [];
  public allValidations: any = {};
  public lang: any = {};
  selectedLanguage: any;

  childFieldKeys: string[] = [];

  childForm: FormGroup = new FormGroup({});

  constructor(private formBuilder: FormBuilder, private _CustomDatePipe: CustomDatePipe) {
    const today = new Date();
    this.minDate = new Date();
    this.minDate.setFullYear(today.getFullYear() - 25);
    this.maxDate = new Date();

    this.bsConfig = {
      containerClass: 'theme-default',
      dateInputFormat: 'MM-DD-YYYY',
      minDate: this.minDate,
      maxDate: this.maxDate
    };
  }

  get f(): { [key: string]: AbstractControl } {
    return this.childForm.controls;
  }

  ngOnInit(): void {
    this.languageTokensArray = this.formConfigData;
    this.allValidations = this.allValidationsFormConfig;

    // Use the passed config; fall back to the imported constant if parent hasn't set it yet
    let config = (this.childFieldsConfig && Object.keys(this.childFieldsConfig).length > 0)
      ? this.childFieldsConfig
      : defaultFormFieldsUpdated?.child_modal_fields;

    if (this.currFormType != 'viking') {
      const { child_add_to_plan, ...rest } = config ?? {};
      config = rest;
    }

    if (config && Object.keys(config).length > 0) {
      this.childFieldsConfig = config;
      this.buildChildFormFromConfig();
    } else {
      this.buildDefaultForm();
    }

    this.childForm.patchValue(this.editChildData || {});
    this.setupSpecialValidators();

    this.lang = {};
    this.selectedLanguage = sessionStorage.getItem('language');
    for (let token of (this.languageData || [])) {
      this.lang[token.key] = token[this.selectedLanguage];
    }
  }

  buildChildFormFromConfig() {
    const controls: any = {};
    this.childFieldKeys = Object.keys(this.childFieldsConfig);

    this.childFieldKeys.forEach(key => {
      const field = this.childFieldsConfig[key];
      const isRequired = Array.isArray(field.required) && field.required.length > 0;
      controls[field.control_name] = ['', isRequired ? Validators.required : null];
    });

    // graduationDay is always needed — conditionally required when post-secondary is checked
    if (!controls['graduationDay']) {
      controls['graduationDay'] = [''];
    }

    this.childForm = this.formBuilder.group(controls);
  }

  buildDefaultForm() {
    this.childFieldKeys = ['child_first_name','child_last_name','child_gender','child_dob',
      'has_child_COB','child_carrier','dependent_with_special_needs','post_secondary_student',
      ...(this.currFormType === 'viking' ? ['child_add_to_plan'] : [])];
    const defaultConfig = defaultFormFieldsUpdated?.child_modal_fields || {};
    if (this.currFormType !== 'viking') {
      const { child_add_to_plan, ...rest } = defaultConfig;
      this.childFieldsConfig = rest;
    } else {
      this.childFieldsConfig = defaultConfig;
    }
    this.childForm = this.formBuilder.group({
      firstName: ['', Validators.compose([Validators.required, Validators.minLength(1)])],
      lastName: ['', Validators.compose([Validators.required, Validators.minLength(1)])],
      gender: ['', Validators.required],
      date_of_birth: ['', Validators.required],
      is_child_having_healthcard: ['', Validators.required],
      child_carrier_name: [''],
      isDisabled: [''],
      enrolledInUniversity: [''],
      graduationDay: [''],
      addedToPlan: ['']
    });
  }

  setupSpecialValidators() {
    const dobControl = this.childForm.get('date_of_birth');
    if (dobControl) {
      dobControl.setValidators([
        Validators.required,
        childrenminimumAgeValidator(this.allValidations),
        this.validateDateforGraduationandBirth
      ]);
      dobControl.updateValueAndValidity();
    }

    this.childForm.get('enrolledInUniversity')?.valueChanges.subscribe(() => {
      this.childForm.get('date_of_birth')?.markAsTouched();
      this.childForm.get('date_of_birth')?.updateValueAndValidity();
    });

    if (this.editChildData?.enrolledInUniversity === true) {
      this.childForm.get('graduationDay')?.setValidators(
        Validators.compose([Validators.required, this.validateDateforGraduation])
      );
    }

    if (this.editChildData?.is_child_having_healthcard === 'true') {
      this.childForm.get('child_carrier_name')?.setValidators([Validators.required]);
      this.childForm.get('child_carrier_name')?.updateValueAndValidity();
    } else {
      this.childForm.get('child_carrier_name')?.clearValidators();
      this.childForm.get('child_carrier_name')?.updateValueAndValidity();
    }
  }

  isChildFieldVisible(field: any): boolean {
    if (!field?.visible_if) return true;
    const vi = field.visible_if;
    const controlVal = this.childForm?.get(vi.field)?.value;
    // loose equality handles string vs boolean
    return controlVal == vi.value;
  }

  onRadioChange(controlName: string, event: any) {
    if (controlName === 'is_child_having_healthcard') {
      this.havingchildrenDependentInsurence(event);
    }
  }

  onCheckboxChange(controlName: string, event: any) {
    if (controlName === 'enrolledInUniversity') {
      this.enrolledunversity(event);
    }
  }

  onSubmit() {
    if (this.childForm.valid) {
      this.submitForm.emit(this.childForm.value);
      this.childForm.reset();
      this.childForm.get('child_carrier_name')?.reset();
      this.childForm.get('child_carrier_name')?.clearValidators();
      this.childForm.get('child_carrier_name')?.updateValueAndValidity();
      this.childForm.get('graduationDay')?.reset();
      this.childForm.get('graduationDay')?.clearValidators();
      this.childForm.get('graduationDay')?.updateValueAndValidity();
      this.show = false;
    }
  }

  onClose() {
    this.close.emit();
    this.show = false;
  }

  public enrolledunversity(event: any) {
    if (event.target.checked === true) {
      this.childForm.get('graduationDay')?.setValidators(
        Validators.compose([Validators.required, this.validateDateforGraduation])
      );
      this.childForm.get('graduationDay')?.updateValueAndValidity();
    } else {
      this.childForm.get('graduationDay')?.reset();
      this.childForm.get('graduationDay')?.clearValidators();
      this.childForm.get('graduationDay')?.updateValueAndValidity();
    }
  }

  public havingchildrenDependentInsurence(event: any) {
    const childrenVal = event.target.value;
    if (childrenVal === 'true') {
      this.childForm.get('child_carrier_name')?.setValidators([Validators.required]);
      this.childForm.get('child_carrier_name')?.updateValueAndValidity();
    } else {
      this.childForm.get('child_carrier_name')?.reset();
      this.childForm.get('child_carrier_name')?.clearValidators();
      this.childForm.get('child_carrier_name')?.updateValueAndValidity();
    }
  }

  validateDateforGraduation = (control: AbstractControl): { [key: string]: any } | null => {
    if (this.childForm) {
      const dobControl = this.childForm.get('date_of_birth');
      if (dobControl) {
        // Both sides parsed to local midnight so the comparison cannot flip on a value
        // that happens to be stored as a 'YYYY-MM-DD' string.
        const dob = toLocalDate(dobControl.value);
        const graduation = toLocalDate(control.value);
        if (dob && graduation && graduation < dob) {
          return { validGraduationDate: true };
        }
      }
    }
    return null;
  };

  validateDateforGraduationandBirth = (control: AbstractControl): { [key: string]: any } | null => {
    if (this.childForm) {
      const gradeControl = this.childForm.get('graduationDay');
      if (gradeControl) {
        const grade = toLocalDate(gradeControl.value);
        const birth = toLocalDate(control.value);
        if (grade && birth && birth > grade) {
          return { validGraduationDateBirth: true };
        }
      }
    }
    return null;
  };

  alphabatesOnly(event: any) {
    // Same rule as the parent form: child names may contain apostrophes, hyphens and accents.
    return isNameKeyAllowed(event);
  }

  numbersOnly(event: any) {
    const key = event.key || String.fromCharCode(event.keyCode);
    return /(^[0-9 \-]+$)/.test(key);
  }

  public checkChildDateofBirth(event: any) {
    if (event.type === 'input') {
      if (event.target.value === '') {
        this.childForm.controls['date_of_birth'].reset();
        this.childForm.controls['date_of_birth'].markAsTouched();
      }
    } else {
      if (event.target.value === 'Invalid date' || event.target.value === '') {
        this.childForm.controls['date_of_birth'].reset();
        this.childForm.controls['date_of_birth'].markAsTouched();
      }
    }
  }

  public checkDateOfgraducation(event: any) {
    if (event.type === 'input') {
      if (event.target.value === '') {
        this.childForm.controls['graduationDay'].reset();
        this.childForm.controls['graduationDay'].markAsTouched();
      }
    } else {
      if (event.target.value === 'Invalid date' || event.target.value === '') {
        this.childForm.controls['graduationDay'].reset();
        this.childForm.controls['graduationDay'].markAsTouched();
      }
    }
  }
}


export function childrenminimumAgeValidator(allValidations: any): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    // Local-midnight parse: new Date('2005-06-14') is UTC midnight, which reads as
    // 13 June in Canada and the USA and pushed children over the age limit a day early.
    const birthDate = toLocalDate(control.value);
    if (!birthDate) return null;
    const today = new Date();
    const parent = control.parent;
    if (!parent) return null;

    const isPostSecondary = parent.get('enrolledInUniversity')?.value === true;

    const maxAge = isPostSecondary
      ? allValidations?.CHILD_AGE_MAX_INSCHOOL ?? 25
      : allValidations?.CHILD_AGE_MAX ?? 21;

    const maxValidBirthDate = new Date(
      today.getFullYear() - maxAge,
      today.getMonth(),
      today.getDate()
    );

    if (birthDate < maxValidBirthDate) {
      return { childrenminimumAge: { maxAge } };
    }

    return null;
  };
}
